import { useEffect, useState } from 'react';
import { getDemo, endDemo, DEMO_CUSTOMER } from '../lib/demo-customer.js';

/**
 * DemoModeBanner — sticky strip shown across the top of every page
 * while a netcom-admin is in demo-customer mode.
 *
 * Without a visible banner it's very easy to forget you're looking at
 * mock data — especially because the dashboard renders identically.
 * This is exactly the kind of cue the ImpersonationBanner pattern uses
 * for real customer impersonation. Demo gets its own (sandbox-blue)
 * variant so it's unmistakable.
 */
export default function DemoModeBanner() {
  // Read demo state on mount + on storage events (in case multiple tabs).
  const [demo, setDemo] = useState(() => getDemo());

  useEffect(() => {
    function refresh() { setDemo(getDemo()); }
    window.addEventListener('storage', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  if (!demo) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-30 bg-sky-600 text-white border-b border-sky-700 px-4 py-1.5 text-[12px] font-semibold flex items-center justify-between gap-3 flex-wrap"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span aria-hidden className="text-[10px] uppercase tracking-wider bg-white/15 px-1.5 py-0.5 rounded">Demo</span>
        <span className="truncate">
          Viewing as <strong>{demo?.customer?.businessName || DEMO_CUSTOMER.businessName}</strong> · sample data only
        </span>
      </div>
      <button
        onClick={() => endDemo()}
        className="text-[11px] font-bold uppercase tracking-wider bg-white/15 hover:bg-white/25 px-2 py-0.5 rounded"
        aria-label="Exit demo mode"
      >
        Exit demo →
      </button>
    </div>
  );
}
